import type { TalentRecord } from './talentDesk'

export const talentBankRetentionDays = 30
export const talentDeskPageSize = 15
const retentionMilliseconds = talentBankRetentionDays * 24 * 60 * 60 * 1000
export type TalentSocialIndicator = 'red' | 'yellow' | 'blue'

export interface TalentBankEntry extends TalentRecord {
  bankId: string
  sourceSheet: string
  registeredAt: string
  socialIndicator?: TalentSocialIndicator
  deletedAt?: string
}

export interface DisqualifiedTalent {
  key: string
  candidateId: string
  name: string
  email?: string
  markedAt: string
  comment?: string
}

export function talentRecordKey(record: Pick<TalentRecord, 'name' | 'role' | 'email' | 'whatsapp'>) {
  if (record.email) return `email:${record.email.trim().toLowerCase()}`
  if (record.whatsapp) return `whatsapp:${record.whatsapp.replace(/\D/g, '')}`
  return `person:${record.name.trim().toLowerCase()}|${record.role.trim().toLowerCase()}`
}

export function markTalentsDisqualified(
  entries: DisqualifiedTalent[],
  candidates: TalentRecord[],
  now = new Date(),
  comment = '',
) {
  const normalizedComment = comment.trim()
  const byKey = new Map(entries.map(entry => [entry.key, entry]))
  const keyByCandidateId = new Map(entries.map(entry => [entry.candidateId, entry.key]))
  for (const candidate of candidates) {
    const key = talentRecordKey(candidate)
    const previousKey = keyByCandidateId.get(candidate.id)
    const existing = byKey.get(key) ?? (previousKey ? byKey.get(previousKey) : undefined)
    if (previousKey && previousKey !== key) byKey.delete(previousKey)
    byKey.set(key, {
      ...existing,
      key,
      candidateId: candidate.id,
      name: candidate.name,
      email: candidate.email,
      markedAt: now.toISOString(),
      comment: normalizedComment || existing?.comment,
    })
    keyByCandidateId.set(candidate.id, key)
  }
  return [...byKey.values()]
}

export function unmarkTalentsDisqualified(entries: DisqualifiedTalent[], candidates: TalentRecord[]) {
  const candidateKeys = new Set(candidates.map(talentRecordKey))
  const candidateIds = new Set(candidates.map(candidate => candidate.id))
  return entries.filter(entry => !candidateKeys.has(entry.key) && !candidateIds.has(entry.candidateId))
}

export function talentSocialIndicator(entry: TalentBankEntry): TalentSocialIndicator {
  return entry.socialIndicator === 'red' || entry.socialIndicator === 'blue' ? entry.socialIndicator : 'yellow'
}

export function updateTalentSocialIndicator(entries: TalentBankEntry[], bankId: string, socialIndicator: TalentSocialIndicator) {
  return entries.map(entry => entry.bankId === bankId ? { ...entry, socialIndicator } : entry)
}

export function talentIsDisqualified(entries: DisqualifiedTalent[], candidate: TalentRecord) {
  const key = talentRecordKey(candidate)
  return entries.some(entry => entry.key === key || entry.candidateId === candidate.id)
}

export function talentIsRegistered(entries: TalentBankEntry[], candidate: TalentRecord) {
  const key = talentRecordKey(candidate)
  return entries.some(entry => !entry.deletedAt && (talentRecordKey(entry) === key || entry.id === candidate.id))
}

export function prioritizeFreshTalents(talents: TalentRecord[], bankEntries: TalentBankEntry[], disqualifiedEntries: DisqualifiedTalent[]) {
  const registeredEntries = bankEntries.filter(entry => !entry.deletedAt)
  const registeredKeys = new Set(registeredEntries.map(talentRecordKey))
  const registeredIds = new Set(registeredEntries.map(entry => entry.id))
  const disqualifiedKeys = new Set(disqualifiedEntries.map(entry => entry.key))
  const disqualifiedIds = new Set(disqualifiedEntries.map(entry => entry.candidateId))
  const fresh: TalentRecord[] = []
  const processed: TalentRecord[] = []

  for (const talent of talents) {
    const key = talentRecordKey(talent)
    const wasProcessed = registeredKeys.has(key) || registeredIds.has(talent.id) || disqualifiedKeys.has(key) || disqualifiedIds.has(talent.id)
    const target = wasProcessed ? processed : fresh
    target.push(talent)
  }

  return [...fresh, ...processed]
}

export function paginateTalents(talents: TalentRecord[], requestedPage: number, pageSize = talentDeskPageSize) {
  const totalPages = Math.max(1, Math.ceil(talents.length / pageSize))
  const page = Math.min(Math.max(1, Math.floor(requestedPage) || 1), totalPages)
  const startIndex = (page - 1) * pageSize
  const items = talents.slice(startIndex, startIndex + pageSize)
  return {
    items,
    page,
    totalPages,
    totalItems: talents.length,
    firstItem: items.length ? startIndex + 1 : 0,
    lastItem: startIndex + items.length,
  }
}

export function pruneTalentBank(entries: TalentBankEntry[], now = new Date()) {
  const cutoff = now.getTime() - retentionMilliseconds
  return entries.filter(entry => !entry.deletedAt || new Date(entry.deletedAt).getTime() > cutoff)
}

export function sortTalentBankEntriesNewestFirst(entries: TalentBankEntry[], dateField: 'registeredAt' | 'deletedAt' = 'registeredAt') {
  return [...entries].sort((left, right) => {
    const leftTime = new Date(left[dateField] ?? left.registeredAt).getTime()
    const rightTime = new Date(right[dateField] ?? right.registeredAt).getTime()
    return (Number.isFinite(rightTime) ? rightTime : 0) - (Number.isFinite(leftTime) ? leftTime : 0)
  })
}

export function registerTalentBankEntries(
  entries: TalentBankEntry[],
  candidates: Array<TalentRecord & { sourceSheet: string }>,
  now = new Date(),
  createId: () => string = () => crypto.randomUUID(),
) {
  const next = pruneTalentBank(entries, now)
  const byKey = new Map(next.map((entry, index) => [talentRecordKey(entry), index]))
  const byCandidateId = new Map(next.map((entry, index) => [entry.id, index]))
  for (const candidate of candidates) {
    const key = talentRecordKey(candidate)
    const existingIndex = byKey.get(key) ?? byCandidateId.get(candidate.id)
    if (existingIndex !== undefined) {
      const existing = next[existingIndex]
      next[existingIndex] = { ...existing, ...candidate, bankId: existing.bankId, registeredAt: now.toISOString(), deletedAt: undefined }
      byKey.set(key, existingIndex)
      byCandidateId.set(candidate.id, existingIndex)
      continue
    }
    byKey.set(key, next.length)
    byCandidateId.set(candidate.id, next.length)
    next.push({ ...candidate, bankId: createId(), registeredAt: now.toISOString(), socialIndicator: 'yellow' })
  }
  return sortTalentBankEntriesNewestFirst(next)
}

export function deleteTalentBankEntry(entries: TalentBankEntry[], bankId: string, now = new Date()) {
  return pruneTalentBank(entries, now).map(entry => entry.bankId === bankId ? { ...entry, deletedAt: now.toISOString() } : entry)
}

export function restoreTalentBankEntry(entries: TalentBankEntry[], bankId: string, now = new Date()) {
  return pruneTalentBank(entries, now).map(entry => entry.bankId === bankId ? { ...entry, deletedAt: undefined } : entry)
}

export function talentBankDaysRemaining(entry: TalentBankEntry, now = new Date()) {
  if (!entry.deletedAt) return talentBankRetentionDays
  return Math.max(0, Math.ceil((new Date(entry.deletedAt).getTime() + retentionMilliseconds - now.getTime()) / (24 * 60 * 60 * 1000)))
}
